import type { ToastTone } from "@/components/ui";
import { translateMessage } from "@/i18n/runtime";
import type { MessageId } from "@/i18n/catalogs/en";

export const actionNotificationEvent = "celsus:action-notification";

export interface ActionNotificationDetail {
  message: string;
  tone: ToastTone;
}

const actionMessages: ReadonlyArray<readonly [RegExp, MessageId]> = [
  [/\/books\/undo\/[^/]+$/, "notifications.libraryUpdated"],
  [/\/books\/bulk$/, "notifications.libraryUpdated"],
  [/\/books\/\d+\/organization$/, "notifications.bookOrganizationUpdated"],
  [/\/books\/\d+\/restore$/, "notifications.bookRestored"],
  [/\/books\/\d+$/, "notifications.bookUpdated"],
  [/\/books\/?$/, "notifications.libraryUpdated"],
  [/\/library\/collections\/\d+$/, "notifications.collectionRemoved"],
  [/\/library\/collections\/?$/, "notifications.collectionCreated"],
  [/\/library\/tags\/\d+$/, "notifications.tagRemoved"],
  [/\/library\/tags\/?$/, "notifications.tagCreated"],
  [/\/journal-entries\/\d+$/, "notifications.journalEntryUpdated"],
  [/\/journal-entries\/?$/, "notifications.journalEntrySaved"],
  [/\/reading-progress\/?$/, "notifications.readingProgressSaved"],
  [/\/reading-sessions\/\d+$/, "notifications.readingSessionUpdated"],
  [/\/reading-sessions\/?$/, "notifications.readingSessionSaved"],
  [/\/reading-goals\/\d+$/, "notifications.readingGoalUpdated"],
  [/\/reading-goals\/?$/, "notifications.readingGoalSaved"],
  [/\/community\/profiles\/me\/avatar$/, "notifications.profilePictureUploaded"],
  [/\/community\/profiles\/me$/, "notifications.communityProfileUpdated"],
  [/\/community\/reviews\/\d+$/, "notifications.reviewUpdated"],
  [/\/community\/reviews\/?$/, "notifications.reviewSaved"],
  [/\/community\/blocks\/\d+$/, "notifications.communitySafetyUpdated"],
  [/\/community\/reports\/?$/, "notifications.reportSubmitted"],
  [/\/groups\/clubs\/\d+\/join$/, "notifications.clubJoined"],
  [/\/groups\/clubs\/?$/, "notifications.clubCreated"],
  [
    /\/groups\/challenges\/\d+\/membership$/,
    "notifications.challengeMembershipUpdated",
  ],
  [
    /\/groups\/notification-preferences$/,
    "notifications.notificationPreferencesUpdated",
  ],
  [/\/recommendations\/preferences$/, "notifications.recommendationPreferencesUpdated"],
  [/\/recommendations\/feedback$/, "notifications.recommendationFeedbackSaved"],
  [/\/library-imports\/\d+\/resolutions$/, "notifications.importChoicesSaved"],
  [/\/library-imports\/\d+\/execute$/, "notifications.libraryImportCompleted"],
  [/\/library-imports\/?$/, "notifications.libraryFileUploaded"],
  [/\/auth\/password\/change$/, "notifications.passwordUpdated"],
  [/\/auth\/sessions\/[^/]+$/, "notifications.sessionRevoked"],
  [/\/auth\/logout-all$/, "notifications.sessionsSignedOut"],
  [/\/users\/me$/, "notifications.profileUpdated"],
];

export function actionSuccessMessage(
  url: string | undefined,
  requestMethod?: string
): string | null {
  if (!url) return null;
  if (/\/recommendations\/events(?:\?|$)/.test(url)) return null;
  const method = requestMethod?.toLowerCase();
  if (/\/books\/?$/.test(url) && method === "post")
    return translateMessage("notifications.bookAdded");
  if (/\/books\/\d+$/.test(url) && method === "delete")
    return translateMessage("notifications.bookRemoved");
  if (/\/library\/collections\/\d+$/.test(url) && method === "delete")
    return translateMessage("notifications.collectionRemoved");
  if (/\/library\/tags\/\d+$/.test(url) && method === "delete")
    return translateMessage("notifications.tagRemoved");
  if (/\/journal-entries\/\d+$/.test(url) && method === "delete")
    return translateMessage("notifications.journalEntryRemoved");
  if (/\/community\/reviews\/\d+$/.test(url) && method === "delete")
    return translateMessage("notifications.reviewRemoved");
  const messageId = actionMessages.find(([pattern]) => pattern.test(url))?.[1];
  return messageId ? translateMessage(messageId) : null;
}

export function actionFailureMessage(action: string, reason: string): string {
  const subject = action
    .replace(/\.$/, "")
    .replace(
      / (added to your library|created|removed|updated|saved|uploaded|submitted|completed|restored)$/,
      ""
    );
  return translateMessage("notifications.actionFailed", { action: subject, reason });
}

export function isActionRequest(method: string | undefined): boolean {
  return ["post", "put", "patch", "delete"].includes(method?.toLowerCase() ?? "");
}

export function dispatchActionNotification(detail: ActionNotificationDetail): void {
  window.dispatchEvent(
    new CustomEvent<ActionNotificationDetail>(actionNotificationEvent, { detail })
  );
}
